import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Currency } from '@app/model/currency';
import { RouteComponent } from '@app/route/route.component';
import { HeaderService } from '@app/service/header/header.service';
import { StateService, WaitThen } from '@app/service/state/state.service';
import Translations from './fi-success.component.json';
import { SelectorItem } from '@app/component/selector/selector.component';
import { ValuePipe } from '@app/shared/pipe/value.pipe';
import { PaymentMethod } from '@app/model/payment-request/payment-request.dto';
import { FiUserService } from '@app/service/fi-user-service/fi-user.service';
import {
  CheckoutType,
  RedirectService,
} from '@app/service/redirect-service/redirect.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'fi-success',
  templateUrl: './fi-success.component.html',
  styleUrls: ['../../page-shared.css', './fi-success.component.scss'],
})
export class FiSuccessComponent extends RouteComponent implements OnInit {
  modalActive: boolean;
  loading: boolean = true;
  amount: number;
  currency: Currency;
  paymentMethod: string;
  successUrl: string;

  selectorItems: SelectorItem[];
  selectedItem: SelectorItem;

  translations: { [key: string]: string };

  public readonly APP_STORE_LINK =
    'https://apps.apple.com/se/app/zaver-handla-på-dina-villkor/id1560858500';
  public readonly PLAY_STORE_LINK =
    'https://play.google.com/store/apps/details?id=io.zaver.app';

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private valuePipe: ValuePipe,
    private fiUserService: FiUserService,
    private redirectService: RedirectService
  ) {
    super(router, route, stateService, headerService);
  }

  async ngOnInit(): Promise<void> {
    this.translations =
      Translations[
        this.fiUserService.getCurrentStateLocaleBaseCode(this.state.language)
      ];
    await this.setup();
  }
  async setup(): Promise<void> {
    this.paymentMethod = this.getMethodTitle();
    this.prepareModalInformation();
    this.tryRedirectToSuccessUrl();
  }

  toggleModalSelectDetailsTab(): void {
    this.selectedItem = this.selectorItems[0];
    this.toggleModal();
  }

  toggleModal(): void {
    this.modalActive = !this.modalActive;
  }

  prepareModalInformation(): void {
    this.selectorItems = [
      {
        tab: this.translations.modalTabSummary,
        title: this.translations.modalTitleSummary,
        lineItems: this.state.paymentInformation?.lineItems,
        totalValue: `${
          this.translations.modalTotalAmountIncVat
        } ${this.valuePipe.transform(this.state.totalCheckoutValue)}`,
      },
    ];
  }

  getMethodTitle(): string {
    switch (this.state.paymentInformation.paymentMethod) {
      case PaymentMethod.INVOICE:
        return this.translations.invoicePaymentMethod;
      case PaymentMethod.CREDIT:
      case PaymentMethod.CREDIT_DIRECT_DEBIT:
        return this.translations.installmentsPaymentMethod;
      default:
        return '';
    }
  }

  getMerchantReturnText(): string {
    return this.translations.returnLink.replace(
      '${MERCHANT}',
      this.state.checkoutCustomizations?.merchantName
    );
  }

  onRouteToSuccessUrl(): void {
    window.location.href = this.successUrl;
  }
  
  async tryRedirectToSuccessUrl(): Promise<void> {
    let redirectSubscription: Subscription;

    // If the readyToLeave endpoint takes to long time,
    // just show the success page after 5 seconds with redirect button.
    // this should be handle by the backend but quick fix for now.
    const timeoutId = setTimeout(() => {
      this.loading = false;
      this.successUrl = this.state.merchantUrls?.onSuccessUrl;

      if (redirectSubscription) {
        redirectSubscription.unsubscribe();
      }
    }, 5000);

    redirectSubscription = (
      await this.redirectService.tryRedirect(CheckoutType.BACKEND_DRIVEN_CHECKOUT, true)
    ).subscribe((stayOrLeave) => {
      switch (stayOrLeave.then) {
        case WaitThen.WAIT:
          break;
        case WaitThen.STAY:
          clearTimeout(timeoutId);
          this.loading = false;
          this.successUrl = this.state.merchantUrls?.onSuccessUrl;
          break;
        case WaitThen.LEAVE:
          clearTimeout(timeoutId);
          this.successUrl = this.state.merchantUrls?.onSuccessUrl;
          // Don't stop the loading there as the redirection is handled 
          break;
      }
    });
  }
}
